import React from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { commonLabels } from "@/i18n";

interface ContactInfoProps {
  darkOverlayOpacity: number;
}

const ContactInfo = ({ darkOverlayOpacity }: ContactInfoProps) => {
  const { language } = useLanguage();
  const labels = commonLabels(language);
  const contactData =
    labels.menuPages?.sellCar?.contactSection?.contactInfo || {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      viewport={{ once: true }}
      className="max-w-4xl mx-auto"
    >
      <h3
        className={`text-3xl font-bold mb-8 text-center transition-colors duration-500 ${
          darkOverlayOpacity > 0.3 ? "text-white" : "text-gray-900"
        }`}
      >
        {contactData.title || "Kontakta Oss Direkt"}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        {contactData.phone && (
          <motion.div
            whileHover={{ scale: 1.05 }}
            className={`flex flex-col items-center p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 ${
              darkOverlayOpacity > 0.3
                ? "bg-white/10 backdrop-blur-sm border border-white/20"
                : "bg-white"
            }`}
          >
            <Phone className="w-12 h-12 text-blue-600 mb-4" />
            <h4
              className={`text-xl font-semibold mb-2 transition-colors duration-500 ${
                darkOverlayOpacity > 0.3 ? "text-white" : "text-gray-900"
              }`}
            >
              {contactData.phone.title || "Telefon"}
            </h4>
            <p
              className={`mb-2 text-center transition-colors duration-500 ${
                darkOverlayOpacity > 0.3 ? "text-gray-300" : "text-gray-600"
              }`}
            >
              {contactData.phone.description ||
                "Prata direkt med vår säljexpert"}
            </p>
            <p className="text-2xl font-bold text-blue-600">
              {contactData.phone.number || "08 66 90 500"}
            </p>
          </motion.div>
        )}

        {contactData.email && (
          <motion.div
            whileHover={{ scale: 1.05 }}
            className={`flex flex-col items-center p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 ${
              darkOverlayOpacity > 0.3
                ? "bg-white/10 backdrop-blur-sm border border-white/20"
                : "bg-white"
            }`}
          >
            <Mail className="w-12 h-12 text-green-600 mb-4" />
            <h4
              className={`text-xl font-semibold mb-2 transition-colors duration-500 ${
                darkOverlayOpacity > 0.3 ? "text-white" : "text-gray-900"
              }`}
            >
              {contactData.email.title || "E-post"}
            </h4>
            <p
              className={`mb-2 text-center transition-colors duration-500 ${
                darkOverlayOpacity > 0.3 ? "text-gray-300" : "text-gray-600"
              }`}
            >
              {contactData.email.description || "Skicka oss dina frågor"}
            </p>
            <p className="text-lg font-semibold text-green-600">
              {contactData.email.address || "kontakt@bilenia.se"}
            </p>
          </motion.div>
        )}

        {contactData.office && (
          <motion.div
            whileHover={{ scale: 1.05 }}
            className={`flex flex-col items-center p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 ${
              darkOverlayOpacity > 0.3
                ? "bg-white/10 backdrop-blur-sm border border-white/20"
                : "bg-white"
            }`}
          >
            <MapPin className="w-12 h-12 text-purple-600 mb-4" />
            <h4
              className={`text-xl font-semibold mb-2 transition-colors duration-500 ${
                darkOverlayOpacity > 0.3 ? "text-white" : "text-gray-900"
              }`}
            >
              {contactData.office.title || "Huvudkontor"}
            </h4>
            <p className="text-lg font-semibold text-purple-600">
              {contactData.office.address || "Hägerstens Allé 10"}
            </p>
            <p
              className={`transition-colors duration-500 ${
                darkOverlayOpacity > 0.3 ? "text-gray-300" : "text-gray-600"
              }`}
            >
              {contactData.office.city || "129 37 Hägersten, Stockholm"}
            </p>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default ContactInfo;
